import { Radio, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import TabletHeader from '../../../components/Operations/TabletHeader';
import KittingQueueTable from '../../../components/Operations/Kitting/KittingQueueTable';
import ProductionMethodIndicator, {
	KITTING_PRODUCTION_METHOD_COPY,
} from '../../../components/Operations/ProductionMethodIndicator';
import {
	DEFAULT_QUEUE_VIEW,
	KittingQueueSummary,
	parseKittingPrioritization,
	parseKittingQueueView,
} from '../../../components/Operations/Kitting/kittingClassification';
import { useBuildingContext } from '../../../context/BuildingContext';
import {
	KITTING_PRODUCTION_METHOD_STORAGE_KEY,
	productionMethodParam,
	usePersistedProductionMethod,
} from '../../../hooks/usePersistedProductionMethod';
import {
	TABLET_BACK_BUTTON_STYLE,
	TABLET_SEARCH_STYLE,
	TABLET_RADIO_GROUP_STYLE,
	TABLET_FILTER_BUTTON_STYLE,
	TABLET_FILTER_ROW_STYLE,
} from '../../../components/Operations/tabletStyles';

const Kitting = () => {
	const { buildingId } = useBuildingContext();
	const { scope: productionMethodScope, setScope: setProductionMethodScope } =
		usePersistedProductionMethod(KITTING_PRODUCTION_METHOD_STORAGE_KEY);
	const productionMethod = productionMethodParam(productionMethodScope);
	const queryParams = new URLSearchParams(location.search);
	const kittingStatus = queryParams.get('kitting') || 'Pending';
	const queueView = parseKittingQueueView(queryParams.get('queue_view'));
	const prioritization = parseKittingPrioritization(
		queryParams.get('prioritization')
	);
	const navigate = useNavigate();
	const [summary, setSummary] = useState<KittingQueueSummary | undefined>();

	const setParam = (name: string, value: string) => {
		const params = new URLSearchParams(location.search);
		params.set(name, value);
		params.set('page', '1');
		navigate({ search: params.toString() });
	};

	const setKittingStatus = (value: string) => {
		const params = new URLSearchParams(location.search);
		params.set('kitting', value);
		if (value === 'Completed') {
			params.set('queue_view', DEFAULT_QUEUE_VIEW);
		}
		params.set('page', '1');
		navigate({ search: params.toString() });
	};

	const setPrioritization = (value: string) => {
		const params = new URLSearchParams(location.search);
		params.set('prioritization', value);
		params.set('page', '1');
		navigate({ search: params.toString() });
	};

	const onSummaryChange = useCallback((next?: KittingQueueSummary) => {
		if (next) setSummary(next);
	}, []);

	const kitStatusOptions = ['Pending', 'Completed', 'All'];
	// Ready / In progress / Late only list kits that have not been started.
	// Under Kits = Completed those views are empty by construction, so the
	// buttons stay visible (counts read 0) but cannot be tapped.
	const viewsLockedToAll = kittingStatus === 'Completed';
	const activeQueueView = viewsLockedToAll ? DEFAULT_QUEUE_VIEW : queueView;

	useEffect(() => {
		if (viewsLockedToAll && queueView !== DEFAULT_QUEUE_VIEW) {
			setParam('queue_view', DEFAULT_QUEUE_VIEW);
		}
	}, [viewsLockedToAll, queueView]);

	const viewOptions: { value: string; label: string; count?: number }[] = [
		{ value: 'All', label: 'All', count: summary?.all },
		{ value: 'NotStarted', label: 'Ready', count: summary?.ready },
		{ value: 'InProgress', label: 'In progress', count: summary?.in_progress },
		{ value: 'Late', label: 'Late', count: summary?.late },
	];
	const prioritizationOptions = [
		{ value: 'scheduler', label: 'Scheduler' },
		{ value: 'readiness', label: 'Production first' },
		{ value: 'due_date', label: 'Due date' },
	];

	return (
		<main
			data-testid="page-fastlane-kitting-queue"
			className="flex flex-col w-full p-3"
		>
			<TabletHeader
				showOnClickButton
				icon={<ArrowLeftOutlined />}
				buttonText="Back"
				actionButtonStyle={TABLET_BACK_BUTTON_STYLE}
				searchStyle={TABLET_SEARCH_STYLE}
				handleOnClick={() => navigate('/tablet/operations')}
				stationExtra={
					<ProductionMethodIndicator
						scope={productionMethodScope}
						copy={KITTING_PRODUCTION_METHOD_COPY}
						onChange={(next) => {
							setProductionMethodScope(next);
							setParam('page', '1');
						}}
					/>
				}
				onSearch={(value) => {
					queryParams.set('search', String(value));
					if (value) {
						queryParams.set('kitting', 'All');
						queryParams.set('queue_view', DEFAULT_QUEUE_VIEW);
					}
					queryParams.set('page', '1');
					navigate({ search: queryParams.toString() });
				}}
			/>

			<div style={TABLET_FILTER_ROW_STYLE}>
				<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
					<Typography.Text type="secondary" style={{ fontSize: 15 }}>
						Kits
					</Typography.Text>
					<Radio.Group
						value={kittingStatus}
						optionType="button"
						buttonStyle="solid"
						size="large"
						style={TABLET_RADIO_GROUP_STYLE}
						data-testid="fastlane-kit-status-filter"
						onChange={(e) => setKittingStatus(String(e.target.value))}
					>
						{kitStatusOptions.map((option) => (
							<Radio.Button
								key={option}
								value={option}
								style={TABLET_FILTER_BUTTON_STYLE}
								data-testid={`fastlane-kit-status-${option}`}
							>
								{option}
							</Radio.Button>
						))}
					</Radio.Group>
				</span>

				<Radio.Group
					value={activeQueueView}
					optionType="button"
					buttonStyle="solid"
					size="large"
					style={TABLET_RADIO_GROUP_STYLE}
					onChange={(e) => {
						if (viewsLockedToAll) return;
						setParam('queue_view', String(e.target.value));
					}}
				>
					{viewOptions.map((option) => (
						<Radio.Button
							key={option.value}
							value={option.value}
							style={TABLET_FILTER_BUTTON_STYLE}
							disabled={viewsLockedToAll && option.value !== 'All'}
							data-testid={`fastlane-kit-queue-view-${option.value}`}
						>
							{option.count === undefined
								? option.label
								: `${option.label} (${option.count})`}
						</Radio.Button>
					))}
				</Radio.Group>

				<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
					<Typography.Text type="secondary" style={{ fontSize: 15 }}>
						Order
					</Typography.Text>
					<Radio.Group
						value={prioritization}
						optionType="button"
						buttonStyle="solid"
						size="large"
						style={TABLET_RADIO_GROUP_STYLE}
						data-testid="fastlane-kit-prioritization"
						onChange={(e) => setPrioritization(String(e.target.value))}
					>
						{prioritizationOptions.map((option) => (
							<Radio.Button
								key={option.value}
								value={option.value}
								style={TABLET_FILTER_BUTTON_STYLE}
								data-testid={`fastlane-kit-prioritization-${option.value}`}
							>
								{option.label}
							</Radio.Button>
						))}
					</Radio.Group>
				</span>
			</div>

			<div style={{ display: 'flex', flexDirection: 'column' }}>
				<KittingQueueTable
					key={buildingId}
					productionMethod={productionMethod}
					buildingId={buildingId}
					onSummaryChange={onSummaryChange}
				/>
			</div>
		</main>
	);
};

export default Kitting;
